import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Checkbox } from '#/modules/commonComponents/checkbox';
import PermissionMatrix from '#/modules/infra/settings/access/components/PermissionMatrix';
import { buildPermissionSections, needsRead } from '#/modules/infra/settings/access/helper/permissionCatalog';

/**
 * 角色權限：checkbox 矩陣，回傳完整 permission id 清單（PUT 為完整取代）。
 * 連動：勾讀取以外的動作 → 一併勾讀取；取消這些動作不動讀取。
 * 取消讀取 → 同列需要讀取的動作一併取消（沒有讀取就做不了）。
 */
const RolePermissionEditor = ({ catalog, selectedIds, onChange, disabled = false }) => {
  const sections = useMemo(() => buildPermissionSections(catalog), [catalog]);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  function commit(next) {
    onChange([...next]);
  }

  function toggle(perm, row, checked) {
    const next = new Set(selected);
    if (checked) {
      next.add(perm.id);
      if (needsRead(perm) && row.actions.read) {
        next.add(row.actions.read.id);
      }
    } else {
      next.delete(perm.id);
      if (perm.action === 'read') {
        row.perms.filter(needsRead).forEach((other) => next.delete(other.id));
      }
    }
    commit(next);
  }

  function toggleRow(row) {
    const next = new Set(selected);
    const allChecked = row.perms.every((perm) => next.has(perm.id));
    row.perms.forEach((perm) => {
      if (allChecked) {
        next.delete(perm.id);
      } else {
        next.add(perm.id);
      }
    });
    commit(next);
  }

  if (!sections.length) {
    return <p className="text-sm text-muted-foreground">沒有可設定的權限（權限目錄可能尚未建立）。</p>;
  }

  return (
    <PermissionMatrix
      sections={sections}
      countGranted={(perms) => perms.filter((perm) => selected.has(perm.id)).length}
      renderCell={(perm, row) => (
        <Checkbox
          checked={selected.has(perm.id)}
          disabled={disabled}
          onCheckedChange={(value) => toggle(perm, row, value === true)}
          aria-label={perm.name || perm.code}
          title={perm.code}
        />
      )}
      rowActionLabel="全選"
      renderRowAction={(row) => {
        const count = row.perms.filter((perm) => selected.has(perm.id)).length;
        const state = count === 0 ? false : count === row.perms.length ? true : 'indeterminate';
        return (
          <Checkbox
            checked={state}
            disabled={disabled}
            onCheckedChange={() => toggleRow(row)}
            aria-label={`全選${row.label}`}
          />
        );
      }}
    />
  );
};

RolePermissionEditor.propTypes = {
  catalog: PropTypes.array.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default RolePermissionEditor;
